<template>
  <div class="budget-content">
    <vx-card class="mb-10" :noShadow="true">
      <div class="vx-row md:mb-10">
        <div class="vx-col">
          <div class="flex">
            <h5 class="font-medium text-2xl">{{ $t('Bidding') }}</h5>
            <speakol-tooltip
              :tooltipRedirect="'https://speakol.zendesk.com/hc/en-us/articles/360016704060-Bidding-Strategy'"
              :tooltipAction="'Learn more here'"
              :tooltipText="'To set your campaign for success, you need to determine the budget you are willing to spend and consider the pattern of spending.'">
            </speakol-tooltip>
          </div>
          <h5 class="text-base font-normal text-gray-500 mt-3">
            {{ $t('Select the parameters you want to bid on.') }}
          </h5>
        </div>
      </div>

      <div class="vx-row" v-if="value.budget.optimizing_for !== null && actions.length > 1">
        <div class="vx-col md:flex" :class="{'mt-6': actions.length > 1}">
          <div class="flex-initial mb-4 mr-10">
            <label class="vs-input--label pl-0">{{ $t('What are you bidding on ?') }}</label>
          </div>
          <div class="flex-initial mb-4 pt-1 mr-10" v-for="action in actions" :key="'optimizing_for-' + action">
            <vs-radio
              @input="gaBid(action)"
              id="create-campaign-s4-bidding-on-radio-button"
              v-model="value.budget.optimizing_for"
              :vs-name="'optimizing_for-' + adsetIndex"
              :vs-value="action"
              :disabled="editing"
            >
              {{ $t(action) }}
            </vs-radio>
          </div>
        </div>
      </div>

      <div>
        <div
          v-if="value.budget.optimizing_for === 'actions'"
          class="vx-col md:w-1/2 w-full mb-6 mt-6"
        >
          <label>{{ $t("Action") }}</label>
          <p class="mt-3 text-gray-500">{{ $t('The action you want people to take upon seeing your ads') }}</p>
          <v-select
            :name="`step4.adsets.${adsetIndex}.budget.action_type`"
            :data-vv-as="$t('Action')"
            v-model="value.budget.action_type"
            :options="budgetActionTypes"
            :disabled="true"
            :reduce="i => i.id"
            class="baisc-select mt-3"
            :placeholder="$t('Choose an action')"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
          />
          <span v-if="$vErrors.has([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.action_type`)" class="text-danger">
            {{ $vErrors.first([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.action_type`) }}
          </span>
        </div>

        <br>

        <div class="vx-col md:w-1/2 w-full mb-6 mt-6">
          <label for>
            {{ costPerActionLabel }}
            <vx-tooltip v-if="value.budget.optimizing_for === 'actions'" class="tooltip" text="When launching a conversion campaign, you need to bid on cost per action. You must add the Speakol Pixel Code to your landing page to successfully count your leads. Initially, you will be charged based on your CPC bid until the campaign secures its first lead. Then, your campaign will run a dynamic CPC to secure more leads with an optimized CPA value. ">
              <vs-icon class="tooltip-icon" icon-pack="speakol" icon="speakol-info" round></vs-icon>
            </vx-tooltip>
          </label>

          <div>
            <p v-if="!allowAlternativeOption && min_allowed_bid <= 0" class="mt-3 text-gray-500">
              {{ $t('Optional') }}
            </p>

            <p v-else class="mt-3 text-gray-500">
              {{ $t('This amount has to be') }} ${{ min_allowed_bid }} {{ $t('or more.') }}
            </p>
          </div>

          <vx-input-group>
            <template slot="prepend">
              <div class="prepend-text dollar-sign">
                <span>$</span>
              </div>
            </template>

            <vs-input
              id="create-campaign-s4-amount-input"
              type="number"
              :min="min_allowed_bid"
              class="w-full"
              name="cost_per_action"
              v-model="value.budget.cost_per_action"
              @input="fireCPCDebounce"
              v-validate="'numeric|min:0'"
              :danger="$vErrors.has([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.cost_per_action`)"
              :placeholder="costPerActionLabel"
            />
          </vx-input-group>

          <div class="vx-col w-full" v-if="!allowAlternativeOption">
            <p class="vx-col w-full charged-note alternative_cpc_div text-blue-500">
              Meta will spend your budget to generate leads using a high-volume bidding strategy. Set a cost-per-action goal if needed.
              <span><a href="https://www.facebook.com/business/help/491846184627504?id=2196356200683573" target="_blank" rel="noopener noreferrer">Learn more</a></span>
            </p>
          </div>

          <span v-if="$vErrors.has([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.cost_per_action`)" class="text-danger">
            {{ ($vErrors.first([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.cost_per_action`) || []).split(',').slice(-1).pop().replace('Cost Per Action', costPerActionLabel) }}
          </span>
        </div>
      </div>

      <div class="vx-row" v-if="value.budget.optimizing_for === 'actions' && allowAlternativeOption">
        <div class="vx-col md:w-2/3 w-full mt-3 mb-10">
          <h5 class="font-medium text-2xl">{{ $t('Alternative Option') }}</h5>
        </div>
        <div class="vx-col md:w-1/2 w-full">
          <label for>{{ $t('Cost Per Click (CPC)') }}</label>
          <p class="mt-3 text-gray-500">
            {{ $t('This amount has to be') }} ${{ fallback_min_allowed_bid }} {{ $t('or more.') }}
          </p>

          <vx-input-group>
            <template slot="prepend">
              <div class="prepend-text dollar-sign">
                <span>$</span>
              </div>
            </template>

            <vs-input
              type="number"
              :min="fallback_min_allowed_bid"
              class="w-full"
              name="fallback_cpc"
              v-model="value.budget.fallback_cpc"
              @input="fireCPCFallbackDebounce"
              v-validate="'numeric|min:0'"
              :danger="$vErrors.has([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.fallback_cpc`)"
              :placeholder="$t('Cost Per Click (CPC)')"
            />
          </vx-input-group>

          <span v-if="$vErrors.has([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.fallback_cpc`)" class="text-danger">
            {{ ($vErrors.first([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.fallback_cpc`) || []).split(',').slice(-1).pop() }}
          </span>
        </div>
        <div class="vx-col w-full">
          <p class="vx-col md:w-1/2 w-full charged-note alternative_cpc_div text-blue-500">
            {{ $t('alternative_cpc_note') }} <span><a href="https://speakol.zendesk.com/hc/en-us/articles/360016704060-Bidding-Strategy" target="_blank" rel="noopener noreferrer">Learn more</a></span>
          </p>
        </div>
      </div>

      <div v-if="allowPriceFloorCheck">
        <Alerts
          v-if="showPriceFloorWarning() && !$vErrors.has([errors, serverErrors], `step4.adsets.${adsetIndex}`)"
          type="warning"
          :message="`You have chosen some websites with a higher ${bidType} limit. Choose, edit, or confirm your list before continuing.`"
          :action="handleAlertAction"
        />

        <Alerts
          v-if="showPriceFloorConfirmNote()"
          type="info"
          :message="`Your ${bidType} rate will be $${value.budget.cost_per_action}  for all websites except the customized ones you have confirmed. If you want to return to readjust your customization,`"
          :action="handleAlertAction"
        />

        <Alerts
          v-if="showPriceFloorWarning() && $vErrors.has([errors, serverErrors], `step4.adsets.${adsetIndex}`)"
          type="error"
          :message="`You have some websites with high ${bidType} so you have to choose, edit or confirm on this list before continue.`"
          :action="handleAlertAction"
        />
      </div>

      <vs-popup class="price-floor-popup holamundo" :active.sync="popupActive">
        <PriceFloorTable
          v-model="value.budget.custom_bids"
          :adsetIndex="adsetIndex"
          :cost_per_action="value.budget.cost_per_action"
          :domains="price_flooring_check_domains"
          :action="value.budget.optimizing_for"
          :bid_type="bidType"
          :cancel="hidePopup"
          :popupActive="popupActive"
          :isEditing="editing"
          @confirm="priceFloorConfirmed"
          :bidsConfirmed="checkIfCustomBidsConfirmed()"
        />
      </vs-popup>
    </vx-card>

    <vx-card class="mb-10" :noShadow="true">
      <div class="vx-row">
        <div class="vx-col">
          <h5 class="font-medium text-2xl">{{ budgetTypeMeta.label }}</h5>
        </div>
      </div>

      <div>
        <div class="vx-col">
          <p class="mt-3 text-gray-500">
            {{ budgetTypeMeta.note }}
          </p>
        </div>

        <div>

          <div class="budget-label">
            <h4>Budget</h4>
          </div>

          <div>
            <p class="mt-3 text-gray-500">
              {{ minAllowedBudgetMsg }}
            </p>
          </div>

          <div class="vx-row">

            <vx-input-group class="vx-col md:w-1/2 w-full">
              <template slot="prepend">
                <div class="prepend-text dollar-sign">
                  <span>$</span>
                </div>
              </template>

              <vs-input
                id="create-campaign-s4-daily-budget-input"
                type="number"
                :min="getAdsetMinAllowedBudget()"
                class=""
                name="budget_value"
                v-model="value.budget.budget_value"
                v-validate="'numeric|min:0'"
                :danger="$vErrors.has([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.budget_value`)"
                :placeholder="budgetTypeMeta.label"
                @input="checkIfFieldValueAllowed('budget_value')"
              />
            </vx-input-group>
          </div>


          <span v-if="$vErrors.has([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.budget_value`)" class="text-danger">
            {{ ($vErrors.first([errors, serverErrors], `step4.adsets.${adsetIndex}.budget.budget_value`) || []).split(',').slice(-1).pop() }}
          </span>
        </div>

        <div class="vx-col w-full">
          <p class="vx-col md:w-1/2 w-full charged-note text-blue-500">
            {{ $t('* You may be charged somewhere between 5 to 10% more than your desired amount.') }}
          </p>
        </div>

        <div v-if="advertisingChannel.supports_adset_delivery_type" class="vx-col w-full">
          <div class="campaign-delivery-parent">
            <h5 class="">{{ $t('Daily campaign Delivery') }}</h5>
          </div>
            <div class="grid grid-column-3 md:grid-flow-col gap-4">
              <div>
                <vs-radio @input="$gtag.event('Step4_Daily_Balanced')" id="create-campaign-s4-balanced-radiobox" v-model="value.delivery_type" :vs-name="'delivery_type-' + adsetIndex" vs-value="1">Balanced</vs-radio>
                <p class="campaign-delivery-desc text-gray-500 mb-2">
                  {{ $t('Spend the budget throughout the whole day.')}}
                </p>
                 <span v-if="$vErrors.has([errors, serverErrors], `step4.adsets.${adsetIndex}.delivery_type`)" class="text-danger">
              {{ $vErrors.first([errors, serverErrors], `step4.adsets.${adsetIndex}.delivery_type`) }}
            </span>
              </div>

              <div>
                <vs-radio @input="$gtag.event('Step4_Daily_Accelerated')" id="create-campaign-s4-accelerated-radiobox" v-model="value.delivery_type" :vs-name="'delivery_type-' + adsetIndex" vs-value="2">Accelerated</vs-radio>
                <p class="campaign-delivery-desc text-gray-500">
                  {{ $t('Spend the budget as soon as possible.')}}
                </p>
              </div>
          </div>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import Alerts from './components/Alerts.vue'
import PriceFloorTable from './components/PriceFloorTable.vue'
import { mapGetters } from 'vuex'
import biddingTypes from '@/views/campaigns/biddingTypes.js'
import SpeakolTooltip from '@/components/speakol-template/SpeakolTooltip.vue'
import validateBudget from '@/validators/budget'

import {get, unionBy, debounce, isEqual, cloneDeep} from 'lodash'
import { ignoreTargeting } from '@/utils/IgnoreTargeting.js'

export default {
  props: {
    value: {
      required: true,
      type: Object
    },
    advertisingChannel: {
      required: true,
      type: Object
    },
    adsetIndex: {
      required: true,
      type: Number
    },
    serverErrors: {
      required: true
    },
    objective: {
      type: String,
      required: true
    },
    objectiveId: {
      type: Number,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    typeId: {
      type: Number,
      required: true
    },
    industry: {
      type: Number,
      required: true
    },
    mainEvent: {
      type: String | null,
      required: true
    },
    budgetType: {
      type: String,
      required: true
    },
    editing: {
      type: Boolean,
      default: false,
      required: false
    },
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    }
  },
  watch: {
    value: {
      handler (newVal) {
        const customBidsConfirmed = this.showPriceFloorConfirmNote()
        if (get(newVal, 'budget.custom_bids.length', 0) && !customBidsConfirmed) {
          newVal.budget.custom_bids = []
        }
        this.$emit('input', newVal)
      },
      deep: true
    },
    'value.budget.optimizing_for' () {
      this.resetBidAndFlooring()
    },
    'value.budget.budget_value' () {
      const value = parseFloat(this.value.budget.budget_value || 0)

      if (value > 99999.99) {
        alert('The value should be less than  99999.99')
        // this.value.budget.budget_value = 99999.99
        return
      }

      const fraction = value.toString().split('.')[1] || ''
      if (fraction.length > 2) {
        alert('The decimals should be up to 2 digits')
        this.value.budget.budget_value = value.toFixed(2)
      }

      if (this.value.budget.budget_value >= this.getAdsetMinAllowedBudget()) {
        this.$emit('fireReachEstimator', this.adsetIndex)
      } else {
        this.clearReachEstimates()
      }
    },
    'value.budget.cost_per_action' () {
      const value = parseFloat(this.value.budget.cost_per_action || 0)

      if (value > 9999.999) {
        // alert('The value should be less than  9999.999')
        // this.value.budget.cost_per_action = 9999.999
        return
      }

      const fraction = value.toString().split('.')[1] || ''
      if (fraction.length > 3) {
        alert('The decimals should be up to 3 digits')
        // this.value.budget.cost_per_action = value.toFixed(3)
      }

      this.$emit('fireAdsetBidHint', { adsetIndex: this.adsetIndex, status: this.value.budget.cost_per_action === this.min_allowed_bid})
    },
    'value.budget.fallback_cpc' () {
      const value = parseFloat(this.value.budget.fallback_cpc || 0)

      if (value > 9999.999) {
        alert('The value should be less than  9999.999')
        // this.value.budget.fallback_cpc = 9999.999
        return
      }

      const fraction = value.toString().split('.')[1] || ''
      if (fraction.length > 3) {
        alert('The decimals should be up to 3 digits')
        this.value.budget.fallback_cpc = value.toFixed(3)
      }
    }
  },
  components: {
    Alerts,
    PriceFloorTable,
    SpeakolTooltip
  },
  data () {
    return {
      timeout: null,
      fallback_min_allowed_bid: 0.00,
      popupActive: false,
      price_floor_confirmed: false,
      min_allowed_bid: '',
      awaitingCheckingMinbid: false,
      failed_domains: [],
      price_flooring_check_domains: [],
      allowPriceFloorCheck: this.objective !== this.$constants.CAMPAIGN_OBJECTIVES_NAMES.LEAD_FORM,
      allowAlternativeOption: ![
        this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_CONVERSION_LEADS,
        this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_CONVERSION_SALES,
        this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_TRAFFIC
      ].includes(this.objectiveId),
      fireCPCDebounce: debounce(() => {
        this.checkIfFieldValueAllowed('cost_per_action')
      }, +process.env.VUE_APP_DEFAULT_DEBOUNCE_TIME || 1000),
      fireCPCFallbackDebounce: debounce(() => {
        this.checkIfFieldValueAllowed('fallback_cpc')
      }, +process.env.VUE_APP_DEFAULT_DEBOUNCE_TIME || 1000)
    }
  },
  computed: {
    ...mapGetters({
      budgetActionTypes: 'campaign/budgetActionTypes'
    }),

    campaignObjectives () {
      return this.$store.getters['campaign/targetingLookups'].objectives
    },

    budgetTypeMeta () {
      // in 'budget' , 'daily_budget'
      return {
        label: this.budgetType === 'budget' ? this.$t('Budget') : this.$t('Daily Budget'),
        note: this.budgetType === 'budget' ? this.$t('This is the maximum amount of money you would like this adset to spend.') : this.$t('This is the maximum amount of money you would like to spend per day.')
      }
    },
    bidType () {
      const type =  biddingTypes[this.value.budget.optimizing_for] || {}

      return (type.short || '').toUpperCase()
    },
    costPerActionLabel () {
      const type =  biddingTypes[this.value.budget.optimizing_for] || {}

      if (this.value.budget.cost_per_action === 0) {
        return ''
      }

      return type.text || ''
    },
    actions () {
      // Brand Awareness  - (Recommendations / Push notifications / Story)
      // App Installs - (Recommendations / Push notifications / Story)
      // Traffic - (Recommendations / Push notifications / Story)
      // WhatApp & Phone - (Recommendations / Push notifications / Story)
      const isDefault =
        ['Brand Awareness', 'App Installs', 'Traffic', 'Whatsapp - Phone'].includes(this.objective) &&
        ['Recommendations', 'Push Notifications', 'Stories'].includes(this.type)

      const defaultBiddingOnType = ['clicks', 'impressions']  // cpc, cpm

      if (isDefault) {
        return defaultBiddingOnType
      }

      // (Brand awareness / Traffic / WhatsApp & Phone) and of type Inline
      const isInline =
        ['Brand Awareness', 'Traffic', 'Whatsapp - Phone'].includes(this.objective) &&
        ['Inline'].includes(this.type)

      if (isInline) {
        return ['impressions'] // cpm
      }

      // (Brand awareness / App installs / Traffic / WhatsApp & Phone) and of type Video
      const isVideo =
        ['Brand Awareness', 'Conversions', 'App Installs', 'Traffic', 'Whatsapp - Phone'].includes(this.objective) &&
        ['Video'].includes(this.type)

      if (isVideo) {
        return ['videos'] // cpv
      }

      // (Brand awareness / App installs / Traffic / WhatsApp & Phone) and of type Video
      const isConversions =
        ['Conversions', this.$constants.CAMPAIGN_OBJECTIVES_NAMES.FACEBOOK_OUTCOME_CONVERSION_LEADS, this.$constants.CAMPAIGN_OBJECTIVES_NAMES.FACEBOOK_OUTCOME_CONVERSION_SALES].includes(this.objective) &&
        ['Recommendations', 'Push Notifications', 'Stories', this.$constants.CAMPAIGN_TYPES_NAMES.SINGLE_IMAGE].includes(this.type)

      const isLeadFormCampaign = this.objective === this.$constants.CAMPAIGN_OBJECTIVES_NAMES.LEAD_FORM

      if (isConversions || isLeadFormCampaign) {
        return ['actions'] // cpc, cpa
      }

      const advertisingChannelBiddingSupports = []
      if (this.advertisingChannel.supports_budget_optimizing_for_clicks) {
        advertisingChannelBiddingSupports.push('clicks') // cpc
      }

      if (this.advertisingChannel.supports_budget_optimizing_for_impressions) {
        advertisingChannelBiddingSupports.push('impressions') // cpm
      }

      // default return
      return advertisingChannelBiddingSupports.length ? advertisingChannelBiddingSupports : defaultBiddingOnType
    },
    minAllowedBudgetMsg () {
      const campaignMinAllowedBudget = this.getCampaignObjectiveBudget(),
        validatedBudget = validateBudget(this.value.budget.budget_value, campaignMinAllowedBudget, this.startDate, this.endDate)
      return validatedBudget.msg
    }
  },
  created () {
    // set the optimizing_for to the first action and ignore setting it in case editing the adset
    if (this.value.budget.optimizing_for === null && this.actions.length && !this.editing) {
      this.value.budget.optimizing_for = this.actions[0]
    }

    // if in editing camapign or duplicating adset
    // this.cost_per_action_masked = this.value.budget.cost_per_action || null
    // this.fallback_cpc_masked = this.value.budget.fallback_cpc || null
    // this.budget_value_masked = this.value.budget.budget_value || null

    // set action type to null at first
    this.value.budget.action_type = 'Lead'

    // set the action type based on the main event
    if (this.mainEvent !== null && this.objectiveId === this.$constants.CAMPAIGN_OBJECTIVES_IDS.FACEBOOK_OUTCOME_CONVERSION_SALES) {
      this.value.budget.action_type = this.mainEvent ? (this.mainEvent.charAt(0).toUpperCase() + this.mainEvent.slice(1)).replace(/_/g, ' ') : null

      // in case the action type is 'Add to wishlist' then it should be 'Add to wish list'
      if (this.value.budget.action_type === 'Add to wishlist') {
        this.value.budget.action_type = this.budgetActionTypes[Object.keys(this.budgetActionTypes).find(key => this.budgetActionTypes[key].label === 'Add to wish list')].label
      }
    }

    this.resetBidAndFlooring()
  },
  methods: {
    gaBid (action) {
      if (action === 'impressions') {
        this.$gtag.event('Step4_Bid_Imp')
      } else if (action === 'clicks') {
        this.$gtag.event('Step4_Bid_Clicks')
      }
    },
    resetBidAndFlooring () {
      // get the minmum allowed bid
      this.getMinAllowedBid()

      // check the current bid
      this.checkMinbid()

      // in case the optimizer is actions and it will be different
      if (this.value.budget.optimizing_for === 'actions') {
        this.getMinAllowedBid('cpc', 'fallback_min_allowed_bid')
        this.checkMinbid('cpc', 'fallback_cpc')
      }

      // clear the errors
      this.$validator.errors.clear()

      // reset the confirmation
      this.price_floor_confirmed = false
    },
    checkMinbidDelayed (bid_type = null, fieldError = null) {
      if (fieldError === null) {
        this.resetConfirmedAdsetsMinBids()
      }

      if (!this.awaitingCheckingMinbid) {
        if (this.timeout) {
          clearTimeout(this.timeout)
          this.timeout = null
        }

        this.timeout = setTimeout(() => {
          this.checkMinbid(bid_type, fieldError)
          this.awaitingCheckingMinbid = false
        }, 100)
      }
      this.awaitingCheckingMinbid = true
    },
    async checkMinbid (bid_type = null, fieldError = null) {
      if (this.advertisingChannel.id !== this.$constants.CAMPAIGN_ADVERTISING_CHANNELS_IDS.SPEAKOL) {
        return
      }

      if (bid_type === null) {
        bid_type = this.bidType.toLowerCase()
      }

      let value = this.value.budget.cost_per_action

      if (fieldError === null) {
        fieldError = 'cost_per_action'
      }

      if (fieldError === 'fallback_cpc') {
        value = parseFloat(this.value.budget.fallback_cpc).toFixed(3)
      }

      // need to remove custom bids
      // this.value.budget.custom_bids = []

      // validate and format the cost_per_action value
      value = parseFloat(value).toFixed(3)

      // reset the errors and price flooring initial state
      this.failed_domains = []
      this.price_floor_confirmed = false
      this.price_flooring_check_domains = []
      this.serverErrors.remove(`step4.adsets.${this.adsetIndex}.budget.${fieldError}`)
      this.errors.remove(`step4.adsets.${this.adsetIndex}.budget.${fieldError}`)

      if (Number.isNaN(value) || !(value > 0)) {
        value = parseFloat(0).toFixed(3)
        return
      }

      try {
        const id = this.value.id || null,
          adset_id = !this.hasCustomBidsCriteriaChanged() ? id : null

        const data = {
          bid_type,
          bid_value: value,
          advertising_channel_id: this.advertisingChannel.id,
          industry_id: this.industry,
          countries: this.value.locations.countries,
          objective_id: this.objectiveId,
          type_id: this.typeId,
          adset_id
        }

        // in this case the campaign has no platforms and target devices
        if (!ignoreTargeting(this.objective, this.type)) {
          data.platforms = {
            websites: this.value.target_platforms.websites,
            apps: this.value.target_platforms.apps
          }
        }

        const response = await window.axios.post(
          '/campaigns/checks/min-bid',
          data
        )

        /**
         * Please note that any change in this property "price_flooring_check_domains" will be watched in
         * => src/views/campaigns/components/steps/components/BudgetContent/components/PriceFloorTable.vue
         * and it will trigger a function that empties the "this.custom_bids"
         */
        const failed_domains = get(response, 'data.payload.domains', [])
        if (fieldError === null && !failed_domains.length) {
          this.setCurrentAdsetMinBidConfirmed()
        }

        this.failed_domains = failed_domains
        this.price_flooring_check_domains = this.prepareAdsetPriceFloorDomains()
      } catch (error) {
        if (error.response && error.response.status === 422) {
          let minBidError = ''

          if (error.response.data.errors.hasOwnProperty('bid_value')) {
            minBidError = error.response.data.errors['bid_value'].join(', ')
          }

          this.errors.add({
            field: `step4.adsets.${this.adsetIndex}.budget.${fieldError}`,
            msg: minBidError === '' ? 'The minimum bid is invalid' : minBidError
          })
        }

        this.failed_domains = []
        this.price_flooring_check_domains = []
      }
    },
    async getMinAllowedBid (bid_type = null, update = null) {
      if (bid_type === null) {
        bid_type = this.bidType.toLowerCase()
      }

      if (update === null) {
        update = 'min_allowed_bid'
      }

      const response = await window.axios.post(
        '/campaigns/checks/min-allowed-bid',
        {
          bid_type,
          advertising_channel_id: this.advertisingChannel.id,
          industry_id: this.industry,
          countries: this.value.locations.countries,
          hideLoader: true
        }
      )

      this.bid_type = response.data.payload.bid_type

      if (update === 'min_allowed_bid') {
        this.min_allowed_bid = response.data.payload.min_allowed_bid
      }

      if (update === 'fallback_min_allowed_bid') {
        this.fallback_min_allowed_bid = response.data.payload.min_allowed_bid
      }
    },
    handleAlertAction () {
      this.popupActive = true
    },
    hidePopup () {
      this.popupActive = false
    },
    priceFloorConfirmed () {
      // set that price floor confirmed
      this.price_floor_confirmed = true

      // set this adset min bid confirmed
      this.setCurrentAdsetMinBidConfirmed()
    },
    getSelectedDomainsCount () {
      return get(this.value, 'budget.custom_bids.length', 0)
    },
    checkIfCurrentCustomBidsEqualConfirmedBids (confirmedCustomBids = []) {
      const currentCustomBids = get(this.value, 'budget.custom_bids', [])

      if (!confirmedCustomBids || !confirmedCustomBids.length || !currentCustomBids || !currentCustomBids.length) {
        return false
      }

      Array(confirmedCustomBids).forEach(item => {
        const item_id = +item.id,
          item_bid = +item.bid,
          current_element = Array(currentCustomBids).find(element => +element.id === item_id),
          current_element_id = current_element ? +current_element.id : 0,
          current_element_bid = current_element ? +current_element.bid : 0
        if (!current_element || item_id !== current_element_id || item_bid !== current_element_bid) {
          return false
        }
      })

      return true
    },
    setCurrentAdsetMinBidConfirmed () {
      const adset_index = this.adsetIndex,
        old_data = cloneDeep(get(this.$store, 'state.adsetsDomainsMinBidConfirmed', {})),
        new_data = new Object()
      new_data[adset_index] = {
        confirmed: true,
        selected_domains: cloneDeep(get(this.value, 'budget.custom_bids', [])),
        selected_industry: +this.industry,
        selected_countries: cloneDeep(get(this.value, 'locations.countries', [])),
        selected_domains_count: this.getSelectedDomainsCount()
      }
      this.$store.state.adsetsDomainsMinBidConfirmed = {...old_data, ...new_data}
    },
    resetConfirmedAdsetsMinBids () {
      if (!get(this.$store, 'state.adsetsDomainsMinBidConfirmed', null)) {
        return
      }

      this.$store.state.adsetsDomainsMinBidConfirmed[this.adsetIndex] = {}
    },
    checkIfCustomBidsConfirmed () {
      const current_industry = +this.industry,
        current_countries = get(this.value, 'locations.countries', []),
        current_domains_count = this.getSelectedDomainsCount(),
        confirmed_custom_bids = get(this.$store, `state.adsetsDomainsMinBidConfirmed[${ this.adsetIndex }]`, {}),
        industry_has_not_changed = confirmed_custom_bids.selected_industry === current_industry,
        countries_have_not_changed = isEqual(confirmed_custom_bids.selected_countries, current_countries),
        domains_count_has_not_changed = confirmed_custom_bids.selected_domains_count === current_domains_count,
        is_current_bids_equal_to_confirmed_bids = this.checkIfCurrentCustomBidsEqualConfirmedBids(confirmed_custom_bids.selected_domains)
      return confirmed_custom_bids.confirmed &&
        industry_has_not_changed &&
        countries_have_not_changed &&
        domains_count_has_not_changed &&
        is_current_bids_equal_to_confirmed_bids
    },
    showPriceFloorConfirmNote () {
      const cpc_value = parseFloat(this.value.budget.cost_per_action || 0),
        minAllowedBid = +this.min_allowed_bid
      return cpc_value >= minAllowedBid && this.price_flooring_check_domains.length && !this.showPriceFloorWarning()
    },
    showPriceFloorWarning () {
      const failed_domains_exist = this.checkIfFailedDomainsExist(),
        confirmed_custom_bids_check = this.checkIfCustomBidsConfirmed()
      return failed_domains_exist && !this.price_floor_confirmed && !confirmed_custom_bids_check
    },
    checkIfFailedDomainsExist () {
      const cpc_value = parseFloat(this.value.budget.cost_per_action || 0),
        minAllowedBid = +this.min_allowed_bid
      return cpc_value >= minAllowedBid && this.failed_domains.length
    },
    prepareAdsetPriceFloorDomains () {
      const filtered_custom_bids_had_price_floor = this.getFilteredSavedCustomBidsHavePriceFloor()
      return unionBy(filtered_custom_bids_had_price_floor, this.failed_domains, 'id')
    },
    hasCustomBidsCriteriaChanged () {
      // old campaign and adset criteria
      const new_adset_industry = +this.industry,
        old_adset_industry = +get(window, 'initialCampaignData.industry.id', 0)
      // compare criteria elements
      if (old_adset_industry !== new_adset_industry) {
        return true
      }

      // new campaign and adset criteria
      const new_adset_countries = get(this.value, 'locations.countries', []),
        old_adset_countries = get(window, `initialCampaignData.adsets.${ this.adsetIndex }.countries`, []).map(item => item.id)
      return !isEqual(old_adset_countries, new_adset_countries)
    },
    getSavedCustomBidsHavePriceFloor () {
      if (['campaigns-create', 'campaigns-duplicate'].indexOf(this.$route.name) !== -1) {
        return []
      }

      if (this.hasCustomBidsCriteriaChanged()) {
        return []
      }

      return get(window, `initialCampaignData.adsets.${ this.adsetIndex }.custom_bids_have_price_floor`, [])
    },
    getFilteredSavedCustomBidsHavePriceFloor () {
      // ensure that every domain in saved custom bids exists in the platforms selected by the advertiser
      const websites = get(this.value, 'target_platforms.websites', []),
        apps = get(this.value, 'target_platforms.apps', []),
        all_platforms = websites.concat(apps),
        saved_custom_bids_had_price_floor = this.getSavedCustomBidsHavePriceFloor()
      return saved_custom_bids_had_price_floor.filter(domain => {
        return all_platforms.findIndex(element => +element === +domain.id) > -1
      })
    },
    getCampaignObjectiveBudget () {
      const campaignObjectives = cloneDeep(this.campaignObjectives) || [],
        currentCampaignObjective = campaignObjectives && campaignObjectives.length ? campaignObjectives.find(item => item.id === this.objectiveId) : {},
        campaignMinAllowedBudget = Number(currentCampaignObjective.minAllowedBudget)
      return campaignMinAllowedBudget
    },
    getAdsetMinAllowedBudget () {
      const campaignMinAllowedBudget = this.getCampaignObjectiveBudget(),
        validatedBudget = validateBudget(this.value.budget.budget_value, campaignMinAllowedBudget, this.startDate, this.endDate)
      return Number(validatedBudget.minBudget)
    },
    checkIfFieldValueAllowed (fieldName) {
      // clear old field errors
      this.errors.remove(`step4.adsets.${this.adsetIndex}.budget.${fieldName}`)
      this.serverErrors.remove(`step4.adsets.${this.adsetIndex}.budget.${fieldName}`)

      // validate fields
      switch (fieldName) {
      case 'cost_per_action':
        this.serverErrors.remove(`step4.adsets.${this.adsetIndex}`)
        return this.validateMinAllowedActionValue(fieldName)
      case 'fallback_cpc':
        return this.validateMinAllowedFallbackActionValue(fieldName)
      default:
        this.validateMinAllowedBudgetValue(fieldName)
      }
    },
    validateMinAllowedActionValue (fieldName) {
      const cpc = this.value.budget.cost_per_action,
        minAllowedBid = this.min_allowed_bid
      if (cpc && cpc < minAllowedBid) {
        return this.errors.add({
          field: `step4.adsets.${this.adsetIndex}.budget.${fieldName}`,
          msg: `The minimum allowed ${this.bidType} is $${minAllowedBid}`
        })
      }

      this.checkMinbidDelayed()
    },
    validateMinAllowedFallbackActionValue (fieldName) {
      const fallback_cpc = this.value.budget.fallback_cpc,
        minAllowedFallbackBid = this.fallback_min_allowed_bid
      if (fallback_cpc && fallback_cpc < minAllowedFallbackBid) {
        return this.errors.add({
          field: `step4.adsets.${this.adsetIndex}.budget.${fieldName}`,
          msg: `The minimum allowed ${this.bid_type.toUpperCase()} is $${minAllowedFallbackBid}`
        })
      }

      this.checkMinbidDelayed('cpc', fieldName)
    },
    validateMinAllowedBudgetValue (fieldName) {
      const budget =  this.value.budget.budget_value,
        minAllowedBudget = this.getAdsetMinAllowedBudget()
      if (!budget || minAllowedBudget <= budget) {
        return
      }

      this.errors.add({
        field: `step4.adsets.${this.adsetIndex}.budget.${fieldName}`,
        msg: `The minimum ${this.endDate ? '' : 'daily'} budget has to be $${minAllowedBudget} or more.`
      })
    },
    clearReachEstimates () {
      this.$set(this.value, 'reach_estimates', 0)
    }
  }
}
</script>

<style lang="scss">

.budget-content {
  .customize-tooltip-with-action {
    right: -100px !important;
  }

  .triangle-left {
    right: 100px !important;
  }

  .vs-radio--circle,
  .vs-radio--borde {
    width: 15px !important;
    height: 15px !important;
    position: absolute !important;
    top: 2px !important;
  }
}

.budget-label {
  margin-top: 30px;

  h4 {
    font-size: 16px;
    font-weight: 400;
  }
}

.campaign-delivery-parent {
  margin-bottom: 30px;

  h5 {
    font-size: 16px;
    font-weight: 400;
  }
}

.charged-note {
  margin-top: 16px;
  margin-bottom: 30px;
}

.alternative_cpc_div {
  font-size: 14px;
  line-height: 24px;
  margin-bottom: 11px;

  span {
    a {
      font-weight: 600;
      text-decoration: underline;
    }
  }
}

.warning-alert {
  background-color: #fffbe6;
  width: 100%;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  padding: 10px 10px 10px 30px;

  .warning-icon {
    position: relative;
    left: -12px;
    top: 7px;
    color: #faad14;
  }

  .warning-desc {
    color: rgba(0, 0, 0, 0.65);
    font-size: 12px;
  }

  .warning-action {
    color: black;
    text-decoration: underline;
  }
}

.info-alert {
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  padding: 10px 50px 10px 30px;
  border-radius: 4px;

  .info-icon {
    position: relative;
    left: -12px;
    top: 7px;
    color: #1890ff;
  }

  .info-desc {
    color: rgba(0, 0, 0, 0.65);
    font-size: 12px;
  }

  .info-action {
    color: black;
    text-decoration: underline;
  }
}

.error-alert {
  background: #fff1f0;
  border: 1px solid #ffa39e;
  padding: 10px 50px 10px 30px;
  border-radius: 4px;

  .error-icon {
    position: relative;
    left: -12px;
    top: 7px;
    color: #f5222d;
  }

  .error-desc {
    color: rgba(0, 0, 0, 0.65);
    font-size: 12px;
  }

  .error-action {
    color: black;
    text-decoration: underline;
  }
}

.price-floor-popup .vs-popup {
    width: 830px !important;
  }

@media (max-width: 576px) {
  .con-vs-popup .vs-popup {
    width: 100% !important;
  }
}

.cost-per-action-parent {
  display: flex;

  label {
    margin-right: 8px;
  }
}

.tooltip-icon {
  font-size: 12px !important;
}

.tooltip {
  width: 20px;
  display: inline-block;
}

.campaign-delivery-desc {
  margin-left: 23px;
}
</style>
